import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import { useAuth } from "../../context/AuthContext";
import CustomModal from "../../Components/CustomModal";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";

function CatalogForm({
  setOpen,
  mode = "create",
  hasDescription = true,
  initialValues = { name: "", description: "", is_active: true },
  onSubmit,
}) {
  const [values, setValues] = useState(initialValues);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const handleSave = async () => {
    if (!String(values.name || "").trim()) {
      setSnackMsg("El nombre es obligatorio.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }

    try {
      setLoading(true);
      await Promise.resolve(
        onSubmit?.({
          name: String(values.name || "").trim(),
          description: hasDescription ? String(values.description || "").trim() : undefined,
          is_active: Boolean(values.is_active),
        })
      );
      setSnackMsg(mode === "edit" ? "Registro actualizado." : "Registro creado.");
      setSnackSev("success");
      setSnackOpen(true);
      setTimeout(() => setOpen(false), 250);
    } catch {
      setSnackMsg("No se pudo guardar.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <TextField
          size="small"
          label="Nombre"
          value={values.name}
          onChange={(event) => setValues((prev) => ({ ...prev, name: event.target.value }))}
          required
        />

        {hasDescription && (
          <TextField
            size="small"
            label="Descripcion"
            value={values.description}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, description: event.target.value }))
            }
            multiline
            minRows={2}
          />
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Estado
          </Typography>
          <Switch
            checked={Boolean(values.is_active)}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, is_active: event.target.checked }))
            }
          />
          <Typography variant="body2">{values.is_active ? "Activo" : "Inactivo"}</Typography>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button disabled={loading} onClick={() => setOpen(false)}>
            Cancelar
          </Button>
          <Button variant="contained" disabled={loading} onClick={handleSave}>
            {loading ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Crear"}
          </Button>
        </Stack>
      </Stack>

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}

export default function CatalogCrudPage({
  title,
  subtitle,
  addTabPath,
  addTabLabel,
  service,
  hasDescription = true,
  viewPerms = [],
  managePerms = [],
  icon: Icon,
}) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { isSuperuser, permissions = [] } = useAuth();

  const canView =
    isSuperuser ||
    [...viewPerms, ...managePerms].some((perm) => permissions.includes(perm));
  const canManage =
    isSuperuser || managePerms.some((perm) => permissions.includes(perm));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeOnly, setActiveOnly] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 450);
    return () => clearTimeout(timeout);
  }, [search]);

  const loadRows = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    try {
      const data = await service.list({
        search: debouncedSearch || undefined,
        active: activeOnly ? "true" : "false",
      });
      setRows(Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : []);
    } catch {
      setRows([]);
      showSnack("No se pudieron cargar los registros.", "error");
    } finally {
      setLoading(false);
    }
  }, [activeOnly, canView, debouncedSearch, service]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const handleCreate = async (payload) => {
    const body = hasDescription
      ? payload
      : { name: payload.name, is_active: payload.is_active };
    await service.create(body);
    showSnack("Registro creado correctamente.", "success");
    await loadRows();
  };

  const handleEdit = async (payload) => {
    if (!selectedItem?.id) return;
    const body = hasDescription
      ? payload
      : { name: payload.name, is_active: payload.is_active };
    await service.update(selectedItem.id, body);
    showSnack("Registro actualizado correctamente.", "success");
    await loadRows();
  };

  const handleToggle = useCallback(
    async (row) => {
      try {
        await service.toggleActive(row.id, !row.is_active);
        showSnack(row.is_active ? "Registro desactivado." : "Registro activado.", "success");
        await loadRows();
      } catch {
        showSnack("No se pudo actualizar el estado.", "error");
      }
    },
    [loadRows, service]
  );

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "name", headerName: "Nombre", flex: 1, minWidth: 220 },
      ...(hasDescription
        ? [{ field: "description", headerName: "Descripcion", flex: 1, minWidth: 220 }]
        : []),
      {
        field: "is_active",
        headerName: "Estado",
        width: 120,
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={value ? "Activo" : "Inactivo"}
            color={value ? "success" : "default"}
            variant={value ? "filled" : "outlined"}
          />
        ),
      },
      {
        field: "actions",
        headerName: "Acciones",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Editar">
              <span>
                <IconButton
                  size="small"
                  disabled={!canManage}
                  onClick={() => {
                    setSelectedItem(row);
                    setEditOpen(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_active ? "Desactivar" : "Activar"}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canManage}
                  color={row.is_active ? "error" : "success"}
                  onClick={() => handleToggle(row)}
                >
                  {row.is_active ? (
                    <BlockIcon fontSize="small" />
                  ) : (
                    <CheckCircleIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [canManage, handleToggle, hasDescription]
  );

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver este modulo.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText title={title} subtitle={subtitle} icon={Icon} width="50%"/>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refrescar listado">
            <IconButton onClick={loadRows}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManage && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
              Crear
            </Button>
          )}
        </Stack>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Buscar por nombre..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={activeOnly}
            onChange={(event) => setActiveOnly(event.target.checked)}
          />
          <Typography variant="body2">{activeOnly ? "Activos" : "Inactivos"}</Typography>
        </Stack>
      </Stack>

      <Box sx={{ height: isMobile ? "calc(100vh - 250px)" : "calc(100vh - 290px)", width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          density={isMobile ? "compact" : "standard"}
        sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#757B2B",
              color: "#ffffff",
            },
            "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover":
            {
              backgroundColor: "#757B2B !important",
            },
            "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator":
            {
              color: "#ffffff !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(16, 23, 60, 0.08)",
            },
          }}
        />
      </Box>

      <CustomModal
        open={createOpen}
        setOpen={setCreateOpen}
        title={`Crear ${title.toLowerCase()}`}
        component={CatalogForm}
        componentProps={{
          mode: "create",
          hasDescription,
          onSubmit: handleCreate,
          initialValues: { name: "", description: "", is_active: true },
        }}
        width={isMobile ? "92%" : "520px"}
      />

      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title={`Editar ${title.toLowerCase()}`}
        component={CatalogForm}
        componentProps={{
          mode: "edit",
          hasDescription,
          onSubmit: handleEdit,
          initialValues: {
            name: selectedItem?.name || "",
            description: selectedItem?.description || "",
            is_active: selectedItem?.is_active ?? true,
          },
        }}
        width={isMobile ? "92%" : "520px"}
      />

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}


